import { Box, Typography } from "@mui/material";
import { ExchangeMealPlanLegacyDisplaySettings, MacroMealPlanLegacyDisplaySettings } from "shared-ui/DisplaySettings/utils";
import { ExportActivityItem } from "shared-ui/Activity";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { genericSort } from "@notemeal/utils-sort";
import { memo, useMemo } from "react";
import { FullMealPlanFragment, RecipeWithServingsFragment } from "../../../types";
import { CurrentMealPlanExportInfo } from "../../../views/Team/Athletes/MealPlans/BulkExportModal";
import { getFoodGroupsComponentsArray } from "./Appendix/utils";
import AppendixRecipe from "./Appendix/Recipe";
import Header from "./Header";
import PagedContainer from "./PagedContainer";
import { isMacro } from "./utils";
import { ExportPdfState } from "./utils/pdfReducer";
import { ExportMealHeader } from "./Meal/ExportMealHeader";
import { ExportMealOption } from "./Meal/ExportMealOption";
import { ExportMealExchangeListsTopRow } from "./Meal/ExchangeListsTopRow";
import { ExportMealExchangeListsBottomRow } from "./Meal/ExchangeListsBottomRow";

const formatDateRange = (mealPlan: FullMealPlanFragment, formatDateWithLocale: (date: Date | string) => string): string => {
  if (mealPlan.startDate && mealPlan.endDate) {
    return ` | ${formatDateWithLocale(mealPlan.startDate)} -> ${formatDateWithLocale(mealPlan.endDate)}`;
  } else {
    return "";
  }
};

// onChangePageDivs should not change on its own (ideally never), i.e. see useCallback if necessary
// This allows memo() to prevent infinite render loop
interface MealPlanExportContainerProps {
  mealPlan: FullMealPlanFragment;
  recipes: readonly RecipeWithServingsFragment[];
  toolbarState: ExportPdfState;
  displaySettings: ExchangeMealPlanLegacyDisplaySettings | MacroMealPlanLegacyDisplaySettings;
  onChangePageDivs: (divs: HTMLDivElement[], mealPlanInfo: CurrentMealPlanExportInfo) => void;
}

const MealPlanExportContainer = ({ mealPlan, displaySettings, toolbarState, onChangePageDivs, recipes }: MealPlanExportContainerProps) => {
  const { formatDateWithLocale } = useDateFormatting();

  const handleDateFormatRangeWithLocale = (mealplan: FullMealPlanFragment) => {
    return formatDateRange(mealplan, formatDateWithLocale);
  };

  const exchangeSettings = displaySettings.__typename === "ExchangeMealPlanLegacyDisplaySettings" ? displaySettings : null;
  const macroSettings = displaySettings.__typename === "MacroMealPlanLegacyDisplaySettings" ? displaySettings : null;

  const { athlete, mealTemplates, activityTemplates, description, promotedFoodGroups, avoidedFoodGroups } = mealPlan;
  const exchangeSet = mealPlan.exchangeSet;

  const activityObjs = activityTemplates.map(at => ({
    start: at.activity.start,
    component: [<ExportActivityItem key={`activity-${at.activity.id}`} activity={at.activity} />],
    exchangeListsComponents: [],
  }));

  const mealObjs = mealTemplates.map(mt => {
    const displayMacros = exchangeSettings?.displayMealMacros || macroSettings?.displayMealMacros || false;
    const displayExchanges = exchangeSettings?.displayMealExchanges || true;
    const displayMealOptions = exchangeSettings?.displayMealOptions || macroSettings?.displayMealOptions || false;
    const displayMealOptionMacros = exchangeSettings?.displayMealOptionMacros || macroSettings?.displayMealOptionMacros || false;

    const mealHeader = <ExportMealHeader
      key={`meal-header-${mt.id}`}
      displayExchanges={displayExchanges}
      displayMacros={displayMacros}
      mealTemplate={mt}
    />;

    const mealOptions = displayMealOptions ? mt.mealOptions : [];
    const mealOptionsChunks = useMemo(() => mealOptions
      .filter(mealOption => mealOption.servingAmounts.length > 0)
      // map option data into its proper component and size them according to orientation
      // 3 options per row in portrait, 5 in landscape
      .map((mealOption, idx) => (
        <Box
          key={mealOption.id}
          sx={{ justifySelf: "center", width: toolbarState.orientation === "portrait" ? "33.33%" : "20%", px: .5 }}>
          <ExportMealOption
            index={idx}
            displayMacros={displayMealOptionMacros}
            recipes={recipes}
            mealOption={mealOption}
            expandRecipes={toolbarState.expandRecipes}
          />
        </Box>
      ))
      // reduce array of meal options into an array of rows of meal options
      .reduce((acc, curr, idx) => {
        const maxOptionsPerRow = toolbarState.orientation === "portrait" ? 3 : 5;
        if (idx % maxOptionsPerRow === 0) {
          acc.push([curr]);
        } else {
          acc[acc.length - 1].push(curr);
        }
        return acc;
      }, [] as React.ReactNode[][])
      // stylize each row with a Box
      .map((row, idx) => (
        <Box
          key={`meal-${mt.id}-opt-row-${idx}`}
          sx={{
            display: "flex",
            flexFlow: "row wrap",
            width: "100%",
            pb: 0.5,
          }}
        >
          {idx === 0 ? mealHeader : null}
          {row}
        </Box>
      )), [mt, toolbarState.orientation, recipes, toolbarState.expandRecipes, displayMealOptionMacros, displayMealOptions]
    );

    return {
      start: mt.meal.start,
      component: mealOptionsChunks.length > 0 ? [ ...mealOptionsChunks ] : [ mealHeader ],
      exchangeListsComponents:
        !toolbarState.condensed && exchangeSettings?.displayExchangeLists && !isMacro(mt) ? [
          <ExportMealExchangeListsTopRow
            key={`plate-${mt.id}-top-exchange-list`}
            mealTemplate={mt}
            exchangeSet={exchangeSet}
            displaySecondaryExchanges={toolbarState.displaySecondaryExchanges}
            recipes={recipes}
          />,
          <ExportMealExchangeListsBottomRow
            key={`plate-${mt.id}-bottom-exchange-list`}
            mealTemplate={mt}
            exchangeSet={exchangeSet}
            displaySecondaryExchanges={toolbarState.displaySecondaryExchanges}
            recipes={recipes}
          />
        ] : [],
    };
  });

  const descriptionComponent = description ? (
    <Box key={`export-mpt-description`} sx={{ lineHeight: 0.65, fontStyle: "italic" }}>
      <Typography
        key={`export-mpt-description-typography`}
        sx={{ lineHeight: 0.8, fontSize: "10px", color: "grey.600" }}
        variant="subtitle1"
      >
        {description}
      </Typography>
    </Box>
  ) : null;

  const eventComponents = [...mealObjs, ...activityObjs]
    .sort((a, b) => genericSort(a.start, b.start))
    .flatMap(obj => {
      return [...obj.component, ...obj.exchangeListsComponents];
    });

  const recipeComponents = toolbarState.displayRecipes
    ? recipes.filter(r => !!r.steps && r.hasFullAccess).map(r => <AppendixRecipe key={r.id} recipe={r} />)
    : [];

  const promotedFoodGroupComponents = toolbarState.displayFoodGroups ? getFoodGroupsComponentsArray(promotedFoodGroups, "promoted") : [];
  const avoidedFoodGroupComponents = toolbarState.displayFoodGroups ? getFoodGroupsComponentsArray(avoidedFoodGroups, "avoided") : [];

  const singleExchangeListComponents = !isMacro(mealTemplates[0]) && toolbarState.condensed ? [
    <ExportMealExchangeListsTopRow
      key={`plate-${mealTemplates[0].id}-top-exchange-list`}
      mealTemplate={mealTemplates[0]}
      exchangeSet={exchangeSet}
      displaySecondaryExchanges={true}
      recipes={recipes}
    />,
    <ExportMealExchangeListsBottomRow
      key={`plate-${mealTemplates[0].id}-bottom-exchange-list`}
      mealTemplate={mealTemplates[0]}
      exchangeSet={exchangeSet}
      displaySecondaryExchanges={true}
      recipes={recipes}
    />
  ] : [];

  // WARNING!: Make sure that any 'component' included in the meal plan is not wrapped in a React.Fragment
  // We get funky behavior, i.e. the bug fixed in 0066eb550ab5d82b39b59901de6e3c32d89fc9ce
  const sortedComponents = [
    descriptionComponent,
    ...eventComponents,
    ...recipeComponents,
    ...promotedFoodGroupComponents,
    ...avoidedFoodGroupComponents,
    ...singleExchangeListComponents,
  ].filter(c => !!c);

  const header = (
    <Header
      mealPlan={mealPlan}
      athleteName={athlete.firstName + " " + athlete.lastName}
      athleteBirthDate={athlete.birthDate}
      displayTitle={toolbarState.displayTitle}
      displayHeaderExchanges={exchangeSettings?.displayDailyExchanges || false}
      displayHeaderMacros={exchangeSettings?.displayDailyMacros || macroSettings?.displayDailyMacros || false}
      title={`${mealPlan.name}${handleDateFormatRangeWithLocale(mealPlan)}`}
    />
  );

  return (
    <PagedContainer
      header={header}
      sortedComponents={sortedComponents}
      orientation={toolbarState.orientation}
      onChangePageDivs={divs =>
        onChangePageDivs(divs, {
          hasEmail: !!athlete.email,
          name: mealPlan.name,
          athlete: { firstName: athlete.firstName, lastName: athlete.lastName },
        })
      }
    />
  );
};

export default memo(MealPlanExportContainer);
